import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";

import {
  alignItems,
  colors,
  fileTypeConfigs,
  fileTypes,
  storage,
  strCode
} from "../../../../../../../consts";
import { t } from "../../../../../../../services";
import { useDebounce } from "../../../../../../../hooks";
import { fileUtils } from "../../../../../../../utils";

import {
  DiscardModal,
  FileInput,
  FlexContainers,
  IconButton,
  Input,
  Paragraph2,
  SVG,
  WrapperContainer
} from "../../../../../../../components";
import { AddLinkButton, FormWrapper, RemoveButton } from "./styled";

export const ListWithIconsForm = ({
  config,
  iconData,
  thumbnailData,
  configErrors,
  showErrors,
  onChange,
  onChangeIconsData,
  onChangeThumbnailsData,
  onRemove
}) => {
  const [icon, setIcon] = useState();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [thumbnail, setThumbnail] = useState(null);

  const [linkText, setLinkText] = useState(null);
  const [link, setLink] = useState(null);

  const [confirmationModalActive, setConfirmationModalActive] = useState(false);

  const isVideo = useMemo(() => {
    const type = icon?.type ?? null;

    return fileTypeConfigs[fileTypes.video].extensions.includes(type);
  }, [icon]);

  const hasLink = useMemo(() => {
    return linkText != null && link != null;
  }, [linkText, link]);

  useEffect(() => {
    setIcon(iconData?.icon);
    setThumbnail(thumbnailData?.media ?? null);

    setTitle(config?.title ?? "");
    setDescription(config?.description ?? "");

    setLinkText(config?.linkText ?? null);
    setLink(config?.link ?? null);
  }, [config, iconData, thumbnailData]);

  useEffect(() => {
    if (!isVideo) {
      setTimeout(() => {
        setThumbnail(null);
      });
    }
  }, [isVideo]);

  const saveConfig = () => {
    const newConfig = { id: config?.id, title, description, link, linkText };
    onChange(newConfig);
  };

  const saveIcon = () => {
    const mediaExtension = fileUtils.getFileExtention(icon);

    const newIconData = { id: config?.id, icon, mediaExtension };
    onChangeIconsData(newIconData);
  };

  const saveThumbnail = () => {
    const mediaExtension = fileUtils.getFileExtention(thumbnail);

    const newThumbnailData = { id: config?.id, media: thumbnail, mediaExtension };
    onChangeThumbnailsData(newThumbnailData);
  };

  const onAddLink = () => {
    setLinkText("");
    setLink("");
  };

  const onRemoveLink = () => {
    setLinkText(null);
    setLink(null);
  };

  useDebounce(saveConfig, 100, title, description, link, linkText);
  useDebounce(saveIcon, 100, icon);
  useDebounce(saveThumbnail, 100, thumbnail);

  return (
    <>
      <FormWrapper
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        gap={16}
        width={"100%"}>
        <WrapperContainer Container={FlexContainers.FlexRowStart} gap={16} width={"100%"}>
          <FileInput
            id="media"
            title={t(strCode.MEDIA_TITLE)}
            file={icon}
            setFile={setIcon}
            fileType={fileTypes.media}
            hasError={showErrors && configErrors?.icon}
            defaultFileName={
              iconData?.id ? `${iconData?.id}.${iconData?.mediaExtension ?? "jpg"}` : null
            }
            warningMsg={t(strCode.RELEASE_NOTE_GLOBAL_INFO)}
            fileFomatErrorMsg={t(strCode.UNSUPPORTED_MEDIA_FORMAT_INFO)}
            onRemoveFile={() => setConfirmationModalActive(true)}
          />

          {isVideo ? (
            <FileInput
              id="thumbnail"
              title={t(strCode.THUMBNAIL_TITLE)}
              file={thumbnail}
              setFile={setThumbnail}
              fileType={fileTypes.image}
              hasError={showErrors && configErrors?.thumbnail}
              defaultFileName={
                thumbnailData?.id
                  ? `${thumbnailData?.id}.${thumbnailData?.mediaExtension ?? "jpg"}`
                  : null
              }
            />
          ) : null}
        </WrapperContainer>

        <Input
          id={"title"}
          title={t(strCode.TITLE)}
          placeholder={t(strCode.TITLE_PLACEHOLDER)}
          value={title}
          setValue={setTitle}
          hasError={showErrors && configErrors?.title}
          maxLength={50}
        />
        <Input
          id={"description"}
          title={t(strCode.SHORT_DESCRIPTION_TITLE)}
          placeholder={t(strCode.ENTER_DESCRIPTION_PLACEHOLDER)}
          value={description}
          setValue={setDescription}
          hasError={showErrors && configErrors?.description}
          maxLength={250}
        />
        {hasLink ? (
          <WrapperContainer Container={FlexContainers.FlexRowCenter} gap={16} width={"100%"}>
            <WrapperContainer width={"60%"} maxWidth={260}>
              <Input
                id={"linkText"}
                title={t(strCode.LINK_TEXT_TITLE)}
                placeholder={t(strCode.LINK_TEXT_TITLE)}
                value={linkText}
                setValue={setLinkText}
                hasError={showErrors && configErrors?.linkText}
                maxLength={50}
              />
            </WrapperContainer>

            <Input
              id={"link"}
              title={t(strCode.LINK_TITLE)}
              placeholder={t(strCode.LINK_TITLE)}
              value={link}
              setValue={setLink}
              hasError={showErrors && configErrors?.link}
              maxLength={100}
            />

            <WrapperContainer mt={24}>
              <IconButton onClick={onRemoveLink}>
                <SVG.DeleteIcon />
              </IconButton>
            </WrapperContainer>
          </WrapperContainer>
        ) : (
          <AddLinkButton onClick={onAddLink}>
            <SVG.LinkIcon />
            <Paragraph2>{t(strCode.ADD_LINK_BTN)}</Paragraph2>
          </AddLinkButton>
        )}
        <RemoveButton onClick={() => onRemove(config?.id)}>
          <SVG.DeleteIcon color={colors.ERROR} />
          {t(strCode.REMOVE_BTN)}
        </RemoveButton>
      </FormWrapper>

      <DiscardModal
        title={strCode.REMOVE_ICON_CONFIRMATION_TITLE}
        text={strCode.REMOVE_ICON_CONFIRMATION_TEXT}
        confirmBtnText={strCode.YES_REMOVE_BTN}
        cancelBtnText={strCode.NO_BTN}
        storageConfirmation={storage.RN_ICON_DELETE_CONFIRMATION}
        active={confirmationModalActive}
        setActive={setConfirmationModalActive}
        onDiscard={() => {
          setConfirmationModalActive(false);
          setIcon(null);
        }}
      />
    </>
  );
};

ListWithIconsForm.propTypes = {
  language: PropTypes.string.isRequired,
  config: {
    id: PropTypes.string.isRequired,
    icon: PropTypes.object.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    link: PropTypes.string,
    linkText: PropTypes.string
  },
  iconData: PropTypes.shape({
    id: PropTypes.number.isRequired,
    icon: PropTypes.object,
    mediaExtension: PropTypes.string
  }),
  thumbnailData: PropTypes.shape({
    id: PropTypes.number.isRequired,
    media: PropTypes.object,
    mediaExtension: PropTypes.string
  }),
  configErrors: PropTypes.shape({
    id: PropTypes.string.isRequired,
    icon: PropTypes.bool.isRequired,
    thumbnail: PropTypes.bool.isRequired,
    title: PropTypes.bool.isRequired,
    description: PropTypes.bool.isRequired,
    link: PropTypes.bool.isRequired,
    linkText: PropTypes.bool.isRequired
  }),
  showErrors: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onChangeIconsData: PropTypes.func.isRequired,
  onChangeThumbnailsData: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  setShowErrors: PropTypes.func.isRequired
};
